import { GetCMSGlobalsQuery } from '@/__generated__/graphql';
import { isBicentenary, isZsl } from '@/lib/utils';
import { GlobalStyle } from '@/theme/globalStyle';
import { getCMSGlobals } from "@api/graphql/queries";
import { ApolloProvider } from "@apollo/client/react";
import { FooterProps } from '@components/organisms/Footer';
import { HeaderProps } from '@components/organisms/Header';
import { Zsl200Banner } from '@components/Zsl200Banner';
import { initializeApollo } from '@lib/apolloClient';
import { getTheme } from "@lib/config";
import { generateCspHeader, generateFeaturePolicyHeader, generatePermissionsPolicyHeader } from "@lib/headers";
import { isBannerSeen } from '@lib/useCookieProBannerTrigger';
import App, { AppContext, AppInitialProps, AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { JsonLd } from "react-schemaorg";
import { WebSite } from "schema-dts";
import Theme, { themes } from 'theme';

const globalProps = {
  __typename: 'DrupalGlobals',
  metadata: {
    __typename: 'Metadata',
    homepageUrl: '/',
    siteName: '',
  },
  hubNavigationLinks: [],
  mainNavigationLinks: [],
  mainNavigationSettings: {
    __typename: "DrupalGobalMainNavigationSettings",
    shopUrl: {
      __typename: "LinkFieldWithTitle",
      url: 'https://shop.zsl.org',
      title: 'Shop',
    },
    ctaUrl: {
      __typename: "LinkFieldWithTitle",
      url: 'https://donate.zsl.org',
      title: 'Donate',
    },
    secondaryUrl: {
      __typename: "LinkFieldWithTitle",
      url: 'https://www.zsl.org/support-us/patrons',
      title: 'Patronage',
    },
    colourTheme: 'default',
  },
  footerLinks: [],
  footerSettings: {
    __typename: "DrupalGlobalsFooterSettings",
    social: [],
    content: {
      __typename: "FormattedField",
      value: '',
      format: '',
      processed: '',
    },
    newsletter: {
      __typename: "NewsletterSignUpBlockParagraph",
      id: '',
      title: '',
      supportingText: null,
      callToAction: {
        __typename: "LinkFieldWithTitle",
        title: '',
        url: '',
        target: null,
      },
    },
    copyright: {
      __typename: "FooterCopyright",
      description: {
        __typename: "FormattedField",
        value: '',
        format: '',
        processed: '',
      },
      links: []
    },
  },
  donationPopup: null,
  hubSettings: {
    __typename: "DrupalHubSettings",
    toMainLabel: '',
    toHubLabel: '',
    hubHomepage: {
      __typename: "LinkField",
      url: '',
    },
    hubPrefix: '',
  },
  nodeDetail: null,
  cspNonce: '',
} as GetCMSGlobalsQuery['globals'];

interface AppCspProps {
  cspNonce: string;
  zsl200BannerSeen: boolean;
}
interface ZSLPageProps {
  theme?: keyof typeof themes|null;
}
interface ZSLAppProps extends AppProps<ZSLPageProps> {
  pageProps: ZSLPageProps;
}

const Footer = dynamic(() => import('@components/organisms/Footer'), {
  ssr: true,
})

const Header = dynamic(() => import('@components/organisms/Header'), {
  ssr: true,
})
function ZSLApp({ Component, pageProps, globals, cspNonce, zsl200BannerSeen }: ZSLAppProps & GetCMSGlobalsQuery & AppCspProps) {
  const client = initializeApollo();
  const [showZsl200Banner, setShowZsl200Banner] = useState(false);
  // Workaround for local dev.
  let theme = process.env.NODE_ENV === 'production' ? getTheme() : (pageProps?.theme || getTheme());
  if (theme === 'zsl' && pageProps?.theme && pageProps?.theme !== 'zsl') {
    theme = pageProps?.theme;
  }
  // Check for an error condition
  const error = (!globals || globals.hasOwnProperty('error'));
  const footerProps: FooterProps = {
    menu: globals.footerLinks,
    ...globals.footerSettings,
  }
  // If the GTM code changes, the SHA-256 hashes need to be updated in the CSP security header in next.config.js.
  const gtmCode = (isZsl(theme) ? 'GTM-523FP7M' : 'GTM-JPGR');
  const gaCode = (isZsl(theme) ? 'G-6XYLLY3DVH' : 'G-MWZNHV9X89');

  useEffect(() => {
    const handleShowBanner = () => setShowZsl200Banner(true);
    window.addEventListener('zsl200:show-banner', handleShowBanner);
    return () => {
      window.removeEventListener('zsl200:show-banner', handleShowBanner);
    };
  }, []);

  const navProps: HeaderProps = {
    ctaDonate: globals.mainNavigationSettings.ctaUrl,
    navigation: {
      open: false,
      searchOpen: false,
      hubMenuItems: globals.hubNavigationLinks,
      menuItems: isBicentenary(theme) ? globals.hubNavigationLinks : globals.mainNavigationLinks,
      ctaOne: globals.mainNavigationSettings.shopUrl,
      ctaTwo: globals.mainNavigationSettings?.secondaryUrl||undefined,
      colourTheme: globals.mainNavigationSettings.colourTheme,
    },
    hubSettings: {
      ...globals.hubSettings,
      hubHomepage: globals.hubSettings.hubHomepage || { url: '/' },
    },
    colourTheme: globals.mainNavigationSettings.colourTheme,
  };

  const enableHeader= () => {
    const disabledHeaderContentTypes = ['campaign', 'donation']
    const nodeBundle = globals?.nodeDetail?.bundle;

    if (nodeBundle) {
      if (disabledHeaderContentTypes.includes(nodeBundle)) {
        return false;
      }
    }
    return true;
  }

  return (
    <>
      {/* <html lang="en"> */}
      <ApolloProvider client={client}>
        <Head>
          {theme === 'zsl' || theme === 'bicentenary' ?
            <link rel="stylesheet" href="https://use.typekit.net/zpa6xot.css" /> :
            <link rel="stylesheet" href="https://use.typekit.net/nur3lqw.css" />}
          {theme === 'bicentenary' &&
             <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
              integrity="sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY="
              crossOrigin=""/>
          }
          <JsonLd<WebSite>
            item={{
              "@context": "https://schema.org",
              "@type": "WebSite",
              name: globals.metadata.siteName,
              "url": globals.metadata.homepageUrl,
              "potentialAction": {
                "@type": "SearchAction",
                "target": `${globals.metadata.homepageUrl}/search?keywords={search_term_string}`,
                "query": "required name=search_term_string" as any
              }
            }}
          />
        </Head>
        {/* Custom GTM embed script with added `gtmNonce` variable. DO NOT MODIFY. */}
        <Script nonce={cspNonce} async id="google-analytics" strategy="afterInteractive">
          {`
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;var n=d.querySelector('[nonce]');
n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));
w.gtmNonce=n.nonce||n.getAttribute('nonce');
f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmCode}');
gtag('get', '${gaCode}', 'client_id', (client_id) => {
  sessionStorage.setItem('client_id', client_id);
});
gtag('get', '${gaCode}', 'session_id', (session_id) => {
  sessionStorage.setItem('session_id', session_id);
});
          `.trim()}
        </Script>
        {/* End Google Tag Manager */}

        {/* Dot Digital */}
        <Script nonce={cspNonce} async id="dot-digital" strategy="afterInteractive">
          {`
          /* ----------------------------------------------------------------------------------------------------
          IMPORTANT!
          The code below requires a developer to install and setup.
          Please do not simply copy/paste this onto your website.
          ---------------------------------------------------------------------------------------------------- */
          // Initiate Web Behaviour Tracking (this section MUST come prior any other dmPt calls)
          // Do not change this
          (function(w,d,u,t,o,c){w['dmtrackingobjectname']=o;c=d.createElement(t);c.async=1;c.src=u;t=d.getElementsByTagName
          (t)[0];t.parentNode.insertBefore(c,t);w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(arguments);};
          })(window, document, '//static.trackedweb.net/js/_dmptv4.js', 'script', 'dmPt');
          window.dmPt('create', 'DM-5105397364-01', 'londonzoo.org,whipsnadezoo.org,zsl.org');

          /* ----------------------------------------------------------------------------------------------------
          Tracks page visit whenever dmPt function is called, e.g. on start up, after ajax request etc
          window.dmPt('track');
          Tracks page visit with custom key values (object with name/value pairs, only primitive types!)
          window.dmPt('track', { customVar: 'Hello', anotherKey: 'World' });
          Tracks page visit with custom email address. dmPt('track') should be called first (any time before, but must be done).
          window.dmPt('track');
          ---------------------------------------------------------------------------------------------------- */


          // window.dmPt("identify", "example@example.com");  // Hardcoded example, inject contact email address.
          // window.dmPt("identify", { email: "example@example.com" }); // Hardcoded example, inject email as object.
          // window.dmPt("identify", { email: "example@example.com", mobileNumber: "00447911123456" }); // Hardcoded example, inject email and mobile-number as object.
          `}
        </Script>
        {/* End Dot Digital */}

        <Theme theme={theme}>
          <GlobalStyle/>
          {isZsl(theme) && !zsl200BannerSeen && <Zsl200Banner visible={showZsl200Banner} onClose={() => setShowZsl200Banner(false)} />}
          {!error && enableHeader() && <Header {...navProps} />}
          <Component
            {...pageProps}
            {...globals.metadata}
            mainNavigationLinks={globals.mainNavigationLinks}
            mainNavigationSettings={globals.mainNavigationSettings}
            donationPopup={globals.donationPopup}
          />
          {!error && <Footer {...footerProps} />}
        </Theme>
        <Script nonce={cspNonce} async data-ot-ignore strategy="afterInteractive" id="frame-break" src="/frame-break.js" />
        {/* Google Tag Manager */}
        {theme && (
          <noscript>
            <iframe src={`https://www.googletagmanager.com/ns.html?id=${gtmCode}`}
                    height="0" width="0" style={{display: 'none', visibility: 'hidden'}}>
            </iframe>
          </noscript>
        )}
        {/* End Google Tag Manager (noscript) */}
        <Script nonce={cspNonce} async id="prospect2" strategy="afterInteractive">
          {`(function(e,t,o,n,p,r,i){e.visitorGlobalObjectAlias=n;e[e.visitorGlobalObjectAlias]=e[e.visitorGlobalObjectAlias]||function(){(e[e.visitorGlobalObjectAlias].q=e[e.visitorGlobalObjectAlias].q||[]).push(arguments)};e[e.visitorGlobalObjectAlias].l=(new Date).getTime();r=t.createElement("script");r.src=o;r.async=true;i=t.getElementsByTagName("script")[0];i.parentNode.insertBefore(r,i)})(window,document,"https://diffuser-cdn.app-us1.com/diffuser/diffuser.js","vgo");
          vgo('setAccount', '28926800');
          vgo('setTrackByDefault', true);
          vgo('process');`}
        </Script>
      </ApolloProvider>
    </>
  );
}

ZSLApp.getInitialProps = async (
  appContext: AppContext
): Promise<AppInitialProps & Omit<GetCMSGlobalsQuery, '__typename'> & AppCspProps> => {
  const client = initializeApollo();

  // Get globals
  const globals = await client
    .query<GetCMSGlobalsQuery>({
      query: getCMSGlobals,
      variables: {
        path:  appContext.ctx.asPath
      },
    })
    .then((res) => {
      return res.data.globals;
    })
    .catch((e) => {
      console.error('[GraphQL] getCMSGlobals failed:', e);
      return {
        error: e,
        ...globalProps,
      };
    });

  // set cache for use with Cloudflare
  if (process.env.NODE_ENV !== 'development') {
    if (appContext.ctx && appContext.ctx.res) {
      // Browser cache for 15m. Cloudflare cache for 1y. Pages will be invalidated on edit
      appContext.ctx.res.setHeader('Cache-Control', 'public, max-age=3600, s-maxage=31536000, stale-while-revalidate=7200, stale-if-error=7200');
    }
  }

  appContext.ctx.res?.setHeader('Content-Security-Policy', generateCspHeader(globals.cspNonce));
  appContext.ctx.res?.setHeader('Feature-Policy', generateFeaturePolicyHeader());
  appContext.ctx.res?.setHeader('Permissions-Policy', generatePermissionsPolicyHeader());

  // calls page's `getInitialProps` and fills `appProps.pageProps`
  let appProps = await App.getInitialProps(appContext);

  // Skip mounting the ZSL 200 banner if already seen.
  const zsl200BannerSeen = isBannerSeen(appContext.ctx.req?.headers.cookie ?? '');

  return {
    cspNonce: globals.cspNonce,
    zsl200BannerSeen,
    ...appProps,
    globals,
  };
};

export default ZSLApp;
